/**
 * SessionSidebar — Collapsible sidebar showing semantic query session history.
 * Allows users to browse, load, and delete past sessions.
 * 
 * Uses sticky positioning within the content flow to avoid overlapping
 * with the main navigation sidebar.
 */
import React, { useEffect } from 'react'
import { Trash2, ChevronLeft, ChevronRight } from '../icons'

function formatRelativeTime(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now - date
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

export function SessionSidebar({
  open,
  onToggle,
  sessions,
  sessionsLoading,
  currentSessionId,
  onSelectSession,
  onDeleteSession,
  onNewSession,
  onRefresh,
}) {
  // Refresh sessions when sidebar opens
  useEffect(() => {
    if (open && onRefresh) onRefresh()
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) {
    return null
  }

  return (
    <aside className="sticky top-0 z-30 flex h-[calc(100vh-60px)] w-[280px] flex-shrink-0 flex-col border-r border-slate-200 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">Query History</div>
          <div className="text-xs text-slate-500">{sessions.length} session{sessions.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onNewSession}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-slate-700"
          >
            + New
          </button>
          <button
            onClick={onToggle}
            className="rounded-lg border border-slate-200 p-1.5 text-slate-500 transition hover:bg-slate-50"
            title="Close sidebar"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {sessionsLoading ? (
          <div className="space-y-2 px-2 py-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-100" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            No sessions yet. Ask your first question to get started.
          </div>
        ) : (
          <div className="space-y-1">
            {sessions.map((session) => {
              const isActive = session.session_id === currentSessionId
              return (
                <div
                  key={session.session_id}
                  className={`group flex items-start gap-2 rounded-xl px-3 py-2.5 transition cursor-pointer ${
                    isActive
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  onClick={() => onSelectSession(session.session_id)}
                >
                  <div className="min-w-0 flex-1">
                    <div className={`truncate text-sm font-medium ${isActive ? 'text-white' : 'text-slate-900'}`}>
                      {session.title || 'Untitled session'}
                    </div>
                    <div className={`mt-0.5 flex items-center gap-2 text-xs ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>
                      <span>{session.message_count || 0} msg{(session.message_count || 0) !== 1 ? 's' : ''}</span>
                      <span>·</span>
                      <span>{formatRelativeTime(session.last_activity || session.created_at)}</span>
                      {session.mode && (
                        <>
                          <span>·</span>
                          <span className="uppercase">{session.mode}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteSession(session.session_id)
                    }}
                    className={`flex-shrink-0 rounded-lg p-1 opacity-0 transition group-hover:opacity-100 ${
                      isActive ? 'text-slate-300 hover:text-white' : 'text-slate-400 hover:text-rose-500'
                    }`}
                    title="Delete session"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </aside>
  )
}
